import React, { useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { MapContainer, TileLayer, GeoJSON, Marker, useMap } from 'react-leaflet';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import GeometryService from '../../services/GeometryService';

const DEFAULT_CENTER = [-15.8, -47.9];
const DEFAULT_ZOOM = 5;

// Configura ícones default do Leaflet para Vite (mesmo padrão do SimpleMapView)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
  iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
  shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

const carStyle = {
  color: '#00bfff',
  weight: 2,
  opacity: 0.8,
  fillColor: '#b3e5fc',
  fillOpacity: 0.2,
};

const sigefStyle = {
  color: '#e53935',
  weight: 2,
  opacity: 0.8,
  fillColor: '#ffcdd2',
  fillOpacity: 0.16,
};

const fallbackStyle = {
  color: '#ff7800',
  weight: 1.5,
  opacity: 0.7,
  fillColor: '#ffe0b2',
  fillOpacity: 0.1,
};

function getFeatureStyle(feature) {
  const source = feature && feature.properties && feature.properties.source;
  if (source === 'sigef') return sigefStyle;
  if (source === 'car') return carStyle;
  return fallbackStyle;
}

function FitBoundsOnFeatures({ features, refreshToken }) {
  const map = useMap();

  useEffect(() => {
    if (!features || !Array.isArray(features) || features.length === 0) {
      return;
    }

    try {
      // Garante que o Leaflet recalcule o tamanho do mapa quando o container muda
      if (map && typeof map.invalidateSize === 'function') {
        map.invalidateSize();
      }

      const layer = L.geoJSON({ type: 'FeatureCollection', features });
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [16, 16] });
      }
    } catch (error) {
      console.error('[ExecutiveDashboardMap] Erro ao ajustar bounds:', error);
    }
  }, [features, map, refreshToken]);

  return null;
}

function MapScreenshotCapture() {
  // No-op: a captura de screenshots será feita por um utilitário off-screen dedicado.
  return null;
}

export default function ExecutiveDashboardMap({
  snapshot,
  forPrint = false,
  onMapReady,
  printRefreshToken,
  onSnapshotReady,
  mapId,
}) {
  const geometryResult = useMemo(() => {
    if (!snapshot || typeof snapshot !== 'object') return null;
    const properties = snapshot.properties || null;
    if (!properties || typeof properties !== 'object') return null;

    const propertyData = {
      properties,
      _metadata: snapshot._metadata || {},
    };

    try {
      return GeometryService.processPropertyGeometries(propertyData);
    } catch (error) {
      console.error('[ExecutiveDashboardMap] Erro ao processar geometrias:', error);
      return null;
    }
  }, [snapshot]);

  const { carFeatures, sigefFeatures, allFeatures, centroid } = useMemo(() => {
    if (!geometryResult) {
      return { carFeatures: [], sigefFeatures: [], allFeatures: [], centroid: null };
    }

    const carGeometries = Array.isArray(geometryResult.carGeometries)
      ? geometryResult.carGeometries
      : [];
    const sigefGeometries = Array.isArray(geometryResult.sigefGeometries)
      ? geometryResult.sigefGeometries
      : [];

    const carFeaturesLocal = carGeometries
      .filter((item) => item && item.geojson)
      .map((item, index) => ({
        type: 'Feature',
        geometry: item.geojson,
        properties: {
          ...(item.metadata || {}),
          source: 'car',
          __index: index,
        },
      }));

    const sigefFeaturesLocal = sigefGeometries
      .filter((item) => item && item.geojson)
      .map((item, index) => ({
        type: 'Feature',
        geometry: item.geojson,
        properties: {
          ...(item.metadata || {}),
          source: 'sigef',
          __index: index,
        },
      }));

    const allFeaturesLocal = [...carFeaturesLocal, ...sigefFeaturesLocal];

    let centroidLocal = null;
    try {
      if (geometryResult.geometries && geometryResult.geometries.length > 0) {
        centroidLocal = GeometryService.calculateCentroid(geometryResult.geometries);
      }
    } catch (error) {
      console.error('[ExecutiveDashboardMap] Erro ao calcular centroide:', error);
    }

    return {
      carFeatures: carFeaturesLocal,
      sigefFeatures: sigefFeaturesLocal,
      allFeatures: allFeaturesLocal,
      centroid: centroidLocal,
    };
  }, [geometryResult]);

  const hasAnyFeature = allFeatures.length > 0;
  const center =
    centroid && typeof centroid.lat === 'number' && typeof centroid.lng === 'number'
      ? [centroid.lat, centroid.lng]
      : DEFAULT_CENTER;
  const zoom = centroid ? 14 : DEFAULT_ZOOM;

  return (
    <Box sx={{ width: '100%', height: '100%', borderRadius: 1, overflow: 'hidden' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        minZoom={3}
        maxZoom={19}
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution="&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          eventHandlers={
            forPrint && typeof onMapReady === 'function'
              ? {
                  load: () => {
                    onMapReady();
                  },
                }
              : undefined
          }
        />

        {carFeatures.length > 0 && (
          <GeoJSON
            data={{ type: 'FeatureCollection', features: carFeatures }}
            style={(feature) => getFeatureStyle(feature)}
          />
        )}

        {sigefFeatures.length > 0 && (
          <GeoJSON
            data={{ type: 'FeatureCollection', features: sigefFeatures }}
            style={(feature) => getFeatureStyle(feature)}
          />
        )}

        {hasAnyFeature && centroid && <Marker position={center} />}

        {hasAnyFeature && (
          <FitBoundsOnFeatures
            features={allFeatures}
            refreshToken={forPrint ? printRefreshToken : undefined}
          />
        )}

        {/* Captura de screenshot apenas no modo tela (forPrint = false) */}
        {!forPrint && hasAnyFeature && onSnapshotReady && mapId && (
          <MapScreenshotCapture mapId={mapId} onSnapshotReady={onSnapshotReady} />
        )}
      </MapContainer>
    </Box>
  );
}
